import { isPatientOrP2LikeRole } from 'shared/utils/constants/p2LikeRoles';

/** Canonical in-app path for the MasterChart product surface. */
export const MASTERCHART_DASHBOARD_PATH = '/dashboard/masterchart';

/**
 * Whether the signed-in account is entitled to MasterChart (paid or free).
 * MasterChart is the patient-product surface: roles patient / p2 / p4, plus admin tiers
 * that mount the same dashboard routes.
 *
 * @param {{ role?: string|null, isGlobalAdmin?: boolean, isRegionalAdmin?: boolean }} [opts]
 * @returns {boolean}
 */
export function hasMasterChartAccess({
  role = null,
  isGlobalAdmin = false,
  isRegionalAdmin = false,
} = {}) {
  if (isGlobalAdmin || isRegionalAdmin) return true;
  if (role === 'admin' || role === 'global_admin' || role === 'regional_admin') {
    return true;
  }
  return isPatientOrP2LikeRole(role);
}

/** @deprecated Prefer MASTERCHART_DASHBOARD_PATH — kept for legacy links and redirects. */
export const MASTERCHART_LEGACY_PATH = '/dashboard/health-records';

export function masterChartPath(subPath = '') {
  if (!subPath) return MASTERCHART_DASHBOARD_PATH;
  return `${MASTERCHART_DASHBOARD_PATH}/${String(subPath).replace(/^\//, '')}`;
}

/**
 * Patient signup / sign-in that lands on MasterChart after auth.
 * AuthPage only honors role=patient|provider|facility and ?next= (not role=p4 / returnTo).
 */
export function masterChartPatientAuthPath() {
  return `/auth?role=patient&next=${encodeURIComponent(MASTERCHART_DASHBOARD_PATH)}`;
}

/** True when the current dashboard route is part of the MasterChart product chrome. */
export function isMasterChartDashboardPath(pathname = '') {
  const path = pathname.replace(/\/$/, '') || '/';
  if (path === MASTERCHART_DASHBOARD_PATH || path.startsWith(`${MASTERCHART_DASHBOARD_PATH}/`)) {
    return true;
  }
  if (path === MASTERCHART_LEGACY_PATH || path.startsWith(`${MASTERCHART_LEGACY_PATH}/`)) {
    return true;
  }
  return path === '/dashboard/medications'
    || path.startsWith('/dashboard/med-reconciliation/')
    || path === '/dashboard/linked-providers';
}

export const MASTERCHART_DRAWER_MATCH_PATHS = [
  MASTERCHART_DASHBOARD_PATH,
  MASTERCHART_LEGACY_PATH,
  '/dashboard/medications',
  '/dashboard/med-reconciliation',
  '/dashboard/linked-providers',
];

/** Relative legacy segment under /dashboard (descendant route paths). */
export const MASTERCHART_LEGACY_RELATIVE_PATH = MASTERCHART_LEGACY_PATH.replace(
  /^\/dashboard/,
  '',
);

/** Build redirect target when leaving a legacy /health-records URL. */
export function masterChartLegacyRedirectTarget(pathname = '', search = '') {
  const legacy = MASTERCHART_LEGACY_PATH;
  const relativeLegacy = MASTERCHART_LEGACY_RELATIVE_PATH;

  let remainder = '';
  if (pathname === legacy || pathname.startsWith(`${legacy}/`)) {
    remainder = pathname.slice(legacy.length);
  } else if (pathname === relativeLegacy || pathname.startsWith(`${relativeLegacy}/`)) {
    remainder = pathname.slice(relativeLegacy.length);
  }

  return `${MASTERCHART_DASHBOARD_PATH}${remainder}${search}`;
}
